import { useState, useEffect } from 'react';
import { type HomeopathyFact } from '../../services/factService';

interface FactModalProps {
  isOpen: boolean;
  fact: HomeopathyFact | null;
  onClose: () => void;
  onRead?: (factId: string) => void | Promise<void>;
  onMarkAsRead?: (factId: string) => void | Promise<void>;
  onMarkAsReadWithPoints?: (factId: string) => boolean | Promise<boolean>; // Nova prop para controle de pontos
  isFirstTime?: boolean;
  hasEarnedPointsToday?: boolean; // Nova prop para indicar se já ganhou pontos hoje
}

export default function FactModal({ 
  isOpen, 
  fact, 
  onClose, 
  onRead, 
  onMarkAsRead, 
  onMarkAsReadWithPoints,
  isFirstTime = false,
  hasEarnedPointsToday = false
}: FactModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [showPoints, setShowPoints] = useState(false);

  // Integração com Guided Tutorial: identificar passo 5.3
  const isTutorialHardMark = ((): boolean => {
    if (typeof document === 'undefined') return false;
    return document.body?.dataset?.tutorialStepId === 'first_run.step_05_03_curiosity_mark_read';
  })();

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShowPoints(false);
      // Previne scroll do body quando modal está aberto
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleRead = () => {
    if (fact) {
      // Se já ganhou pontos hoje, apenas marca como lida sem pontos
      if (hasEarnedPointsToday) {
        const readHandler = onMarkAsRead || onRead;
        if (readHandler) {
          readHandler(fact.id);
        }
        handleClose();
        return;
      }

      // Se tem o handler de pontos, usa ele
      if (onMarkAsReadWithPoints) {
        const earnedPoints = onMarkAsReadWithPoints(fact.id);
        if (earnedPoints) {
          setShowPoints(true);
          // Mostra os pontos por 2 segundos antes de fechar
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          // Não ganhou pontos, apenas fecha
          handleClose();
        }
      } else {
        // Fallback para o comportamento antigo
        const readHandler = onMarkAsRead || onRead;
        if (readHandler) {
          readHandler(fact.id);
          setShowPoints(true);
          
          // Mostra os pontos por 2 segundos antes de fechar
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      }
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'general': return 'ri-information-line';
      case 'history': return 'ri-time-line';
      case 'principles': return 'ri-compass-3-line';
      case 'remedies': return 'ri-medicine-bottle-line';
      case 'research': return 'ri-microscope-line';
      case 'practice': return 'ri-stethoscope-line';
      case 'curiosity': return 'ri-lightbulb-line';
      default: return 'ri-information-line';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'general': return 'bg-gray-100 text-gray-600';
      case 'history': return 'bg-amber-100 text-amber-600';
      case 'principles': return 'bg-blue-100 text-blue-600';
      case 'remedies': return 'bg-green-100 text-green-600';
      case 'research': return 'bg-purple-100 text-purple-600';
      case 'practice': return 'bg-teal-100 text-teal-600';
      case 'curiosity': return 'bg-yellow-100 text-yellow-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'general': return 'Geral';
      case 'history': return 'História';
      case 'principles': return 'Princípios';
      case 'remedies': return 'Remédios';
      case 'research': return 'Pesquisa';
      case 'practice': return 'Prática';
      case 'curiosity': return 'Curiosidade';
      default: return 'Geral';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'basic': return 'bg-emerald-100 text-emerald-600';
      case 'intermediate': return 'bg-orange-100 text-orange-600';
      case 'advanced': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getDifficultyName = (difficulty: string) => {
    switch (difficulty) {
      case 'basic': return 'Básico';
      case 'intermediate': return 'Intermediário';
      case 'advanced': return 'Avançado';
      default: return 'Geral';
    }
  };

  if (!isOpen || !fact) return null;

  return (
    <div className={`
      fixed inset-0 bg-black/50 backdrop-blur-sm ${isTutorialHardMark ? 'z-[10002]' : 'z-50'}
      flex items-center justify-center p-4
      transition-all duration-300
      ${isClosing ? 'opacity-0' : 'opacity-100'}
    `}
    >
      <div className={`
        bg-white rounded-3xl shadow-2xl w-full max-w-md
        transform transition-all duration-300
        ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
      `}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-6 rounded-t-3xl text-white relative overflow-hidden">
          {/* Padrão de fundo */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
          </div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                  <i className="ri-lightbulb-line text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold">
                    {isFirstTime ? 'Bem-vindo!' : 'Curiosidade do Dia'}
                  </h2>
                  <p className="text-white/80 text-sm">Homeopatia</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                style={isTutorialHardMark ? { pointerEvents: 'none' } : undefined}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {isFirstTime && (
              <div className="bg-white/20 rounded-2xl p-3 mb-4">
                <p className="text-sm text-white/90">
                  🎉 Esta é sua primeira curiosidade! A partir de agora, você receberá uma nova curiosidade a cada 24 horas.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {/* Badges de categoria e dificuldade */}
          <div className="flex gap-2 mb-4">
            <span className={`
              px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1
              ${getCategoryColor(fact.category)}
            `}>
              <i className={getCategoryIcon(fact.category)}></i>
              {getCategoryName(fact.category)}
            </span>
            <span className={`
              px-3 py-1 rounded-full text-xs font-medium
              ${getDifficultyColor(fact.difficulty)}
            `}>
              {getDifficultyName(fact.difficulty)}
            </span>
          </div>

          {/* Título */}
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {fact.title}
          </h3>

          {/* Conteúdo */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-6">
            <p className="text-gray-700 leading-relaxed">
              {fact.content}
            </p>
          </div>

          {/* Tags */}
          {fact.tags.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-600 mb-2">Tags:</p>
              <div className="flex flex-wrap gap-2">
                {fact.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Animação de pontos */}
          {showPoints && (
            <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-60">
              <div className="bg-emerald-500 text-white px-6 py-3 rounded-2xl shadow-2xl animate-bounce">
                <div className="flex items-center gap-2">
                  <i className="ri-trophy-line text-xl"></i>
                  <span className="font-bold">+10 pontos!</span>
                </div>
              </div>
            </div>
          )}

          {/* Botão de ação */}
          <button
            onClick={handleRead}
            disabled={showPoints}
            className={`
              w-full py-4 rounded-2xl font-semibold text-white
              transition-all duration-300 flex items-center justify-center gap-2
              ${showPoints 
                ? 'bg-emerald-500 cursor-not-allowed' 
                : hasEarnedPointsToday
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 active:scale-95'
              }
            `}
            data-tutorial-anchor="btn-fact-mark-read"
            style={isTutorialHardMark ? { pointerEvents: 'auto', position: 'relative', zIndex: 10003 } : undefined}
          >
            {showPoints ? (
              <>
                <i className="ri-check-line text-xl"></i>
                Lida! +10 pontos
              </>
            ) : hasEarnedPointsToday ? (
              <>
                <i className="ri-time-line text-xl"></i>
                Seus pontos já foram contabilizados hoje. Volte novamente amanhã.
              </>
            ) : (
              <>
                <i className="ri-book-open-line text-xl"></i>
                Marcar como Lida (+10 pontos)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}